<template>
  <div class="kpi-dashboard">

    <!-- Navigation Menu -->
    <v-card class="nav-menu-card" elevation="2">
      <div class="nav-menu">
        <router-link v-for="(item, index) in menuItems" :key="index" :to="item.route" class="nav-item"
          :class="{ 'active': isActiveRoute(item.route) }">
          <v-icon :color="isActiveRoute(item.route) ? APP_Color : '#718096'" size="20">{{ item.icon }}</v-icon>
          <span class="nav-label">{{ item.label }}</span>
        </router-link>
      </div>
    </v-card>

    <!-- Filtru perioadă -->
    <v-card class="filter-card mb-4" elevation="1">
      <div class="filter-row">
        <div class="filter-group">
          <v-icon size="18" class="mr-1" color="grey">mdi-account</v-icon>
          <span class="filter-label">Agent:</span>
          <v-chip :color="tipAgent === 'Hunter' ? 'deep-orange' : tipAgent === 'Farmer' ? 'green' : 'grey'"
            size="small" class="ml-2">
            {{ utilizatorAfisaj }} — {{ tipAgent || 'Neasignat' }}
          </v-chip>
        </div>

        <div class="filter-group">
          <v-icon size="18" class="mr-1" color="grey">mdi-calendar-month</v-icon>
          <span class="filter-label">Perioadă:</span>

          <v-select v-model="lunaSelectata" :items="luni" item-title="label" item-value="value"
            density="compact" variant="outlined" hide-details class="filter-select ml-2" />

          <v-select v-model="anSelectat" :items="ani" density="compact" variant="outlined" hide-details
            class="filter-select ml-2" />

          <v-btn :color="APP_Color" density="compact" class="ml-3 refresh-btn" :loading="loading"
            @click="incarcaRaport">
            <v-icon size="16" class="mr-1">mdi-refresh</v-icon> Actualizează
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Loading state -->
    <div v-if="loading" class="d-flex justify-center align-center py-8">
      <v-progress-circular indeterminate :color="APP_Color" size="48" />
    </div>

    <!-- Niciun KPI configurat -->
    <v-alert v-else-if="!loading && kpiItems.length === 0" type="info" variant="tonal" class="mb-4">
      <template #title>Niciun KPI configurat</template>
      Nu există KPI-uri active pentru rolul <strong>{{ tipAgent || 'necunoscut' }}</strong>.
      Contactați administratorul pentru a configura planul KPI.
    </v-alert>

    <!-- KPI Cards -->
    <div v-else>
      <!-- Sumar global -->
      <v-row class="mb-4" dense>
        <v-col cols="12" sm="4">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" style="background: rgba(33,150,243,0.1)">
                <v-icon color="#2196F3" size="24">mdi-target</v-icon>
              </div>
              <div>
                <div class="summary-value">{{ kpiItems.length }}</div>
                <div class="summary-label">KPI-uri urmărite</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" style="background: rgba(76,175,80,0.1)">
                <v-icon color="#4CAF50" size="24">mdi-check-circle</v-icon>
              </div>
              <div>
                <div class="summary-value">{{ kpiAtinse }}</div>
                <div class="summary-label">KPI-uri atinse (≥ 100%)</div>
              </div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card class="summary-card" elevation="1">
            <div class="summary-inner">
              <div class="summary-icon" :style="{ background: `rgba(${medieColorRGB}, 0.1)` }">
                <v-icon :color="medieColor" size="24">mdi-chart-donut</v-icon>
              </div>
              <div>
                <div class="summary-value" :style="{ color: medieColor }">{{ medieRealizare }}%</div>
                <div class="summary-label">Medie realizare globală</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- KPI Cards grid -->
      <v-row dense>
        <v-col v-for="kpi in kpiItems" :key="kpi.KPIDefinitieID" cols="12" sm="6" md="4">
          <v-card class="kpi-card" :class="{ 'kpi-card--expanded': expandedID === kpi.KPIDefinitieID }" elevation="1">

            <!-- Header card — click pentru expand -->
            <div class="kpi-card-inner" @click="toggleDetaliu(kpi)" style="cursor:pointer">
              <div class="kpi-header">
                <v-chip :color="kpi.TipAgent === 'Hunter' ? 'deep-orange' : 'green'" size="x-small" label>
                  {{ kpi.TipAgent }}
                </v-chip>
                <v-chip v-if="kpi.TipCalcul === 'Automat'" size="x-small" color="blue" variant="tonal" class="ml-1">
                  Auto
                </v-chip>
                <v-chip v-else size="x-small" color="grey" variant="tonal" class="ml-1">
                  Manual
                </v-chip>
                <v-icon class="ml-auto expand-icon"
                  :class="{ 'expand-icon--open': expandedID === kpi.KPIDefinitieID }"
                  size="18" color="grey">mdi-chevron-down</v-icon>
              </div>

              <div class="kpi-title">{{ kpi.Denumire }}</div>

              <div class="kpi-progress-row">
                <span class="kpi-realizat" :style="{ color: getKPIColor(kpi.ProcentRealizat) }">
                  {{ kpi.Realizat }}
                </span>
                <span class="kpi-separator">/</span>
                <span class="kpi-target">{{ kpi.Target }}</span>
              </div>

              <div class="kpi-progress-bar-wrap">
                <div class="kpi-progress-bar">
                  <div class="kpi-progress-fill"
                    :style="{ width: Math.min(kpi.ProcentRealizat, 100) + '%', background: getKPIColor(kpi.ProcentRealizat) }">
                  </div>
                </div>
                <span class="kpi-percent" :style="{ color: getKPIColor(kpi.ProcentRealizat) }">
                  {{ kpi.ProcentRealizat }}%
                </span>
              </div>
            </div>

            <!-- Expand: detalii -->
            <v-expand-transition>
              <div v-if="expandedID === kpi.KPIDefinitieID" class="kpi-detalii">

                <!-- Loading detalii -->
                <div v-if="loadingDetalii[kpi.KPIDefinitieID]" class="d-flex justify-center py-4">
                  <v-progress-circular indeterminate :color="APP_Color" size="28" />
                </div>

                <!-- Fără date -->
                <div v-else-if="!detalii[kpi.KPIDefinitieID]?.length" class="detalii-empty">
                  <v-icon size="20" color="grey-lighten-1">mdi-information-outline</v-icon>
                  <span class="ml-1">Nicio înregistrare găsită pentru această perioadă.</span>
                </div>

                <!-- Tabel detalii -->
                <div v-else>
                  <div class="detalii-header">
                    <v-icon size="14" color="grey" class="mr-1">mdi-format-list-bulleted</v-icon>
                    {{ detalii[kpi.KPIDefinitieID].length }} înregistrări
                  </div>
                  <div class="detalii-scroll">
                    <table class="detalii-table">
                      <thead>
                        <tr>
                          <th>{{ labelColoanaDetaliu(getSursaDate(kpi)) }}</th>
                          <th v-if="areClient(getSursaDate(kpi))">Client</th>
                          <th v-if="areContact(getSursaDate(kpi))">{{ labelContact(getSursaDate(kpi)) }}</th>
                          <th v-if="areValoare(getSursaDate(kpi))">Valoare est.</th>
                          <th v-if="areStatus(getSursaDate(kpi))">Status</th>
                          <th>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in detalii[kpi.KPIDefinitieID]" :key="row.ID"
                            class="detalii-row-link"
                            @click="navigheazaLaDetaliu(getSursaDate(kpi), row.ID)">
                          <td class="col-denumire">{{ row.Denumire }}</td>
                          <td v-if="areClient(getSursaDate(kpi))" class="col-client">{{ row.Client || '—' }}</td>
                          <td v-if="areContact(getSursaDate(kpi))" class="col-contact">{{ row.Contact || '—' }}</td>
                          <td v-if="areValoare(getSursaDate(kpi))" class="col-valoare">
                            {{ row.Valoare != null ? formatValoare(row.Valoare) : '—' }}
                          </td>
                          <td v-if="areStatus(getSursaDate(kpi))">
                            <v-chip v-if="row.Status" size="x-small" variant="tonal" color="blue">
                              {{ row.Status }}
                            </v-chip>
                            <span v-else class="text-grey">—</span>
                          </td>
                          <td class="col-data">{{ formatData(row.Data) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            </v-expand-transition>

          </v-card>
        </v-col>
      </v-row>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AppSettings from '@/settings/appSettings'
import JwtServices from '@/services/JwtServices'
import CrmKPIService from '@/services/Comercial/CrmKPI'

export default defineComponent({
  name: 'KPIDashboard',

  data() {
    return {
      APP_Color: AppSettings.APP_Color,
      loading: false,
      kpiItems: [] as any[],
      tipAgent: '' as string,

      anSelectat: new Date().getFullYear(),
      lunaSelectata: new Date().getMonth() + 1,

      ani: Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i),
      luni: [
        { label: 'Ianuarie', value: 1 },
        { label: 'Februarie', value: 2 },
        { label: 'Martie', value: 3 },
        { label: 'Aprilie', value: 4 },
        { label: 'Mai', value: 5 },
        { label: 'Iunie', value: 6 },
        { label: 'Iulie', value: 7 },
        { label: 'August', value: 8 },
        { label: 'Septembrie', value: 9 },
        { label: 'Octombrie', value: 10 },
        { label: 'Noiembrie', value: 11 },
        { label: 'Decembrie', value: 12 },
      ],

      // expand detalii
      expandedID: null as string | null,
      detalii: {} as Record<string, any[]>,
      loadingDetalii: {} as Record<string, boolean>,

      menuItems: [
        { label: 'Dashboard', route: '/crm/dashboard', icon: 'mdi-view-dashboard' },
        { label: 'Parteneri', route: '/crm/parteneri', icon: 'mdi-account-tie' },
        { label: 'Contacte', route: '/crm/contacte', icon: 'mdi-account-group' },
        { label: 'Oportunități', route: '/crm/grid-oportunitati', icon: 'mdi-briefcase' },
        { label: 'Activități', route: '/crm/activitati', icon: 'mdi-calendar-check' },
        { label: 'KPI', route: '/crm/kpi', icon: 'mdi-chart-bar' },
      ],
    }
  },

  computed: {
    utilizatorAfisaj(): string {
      return JwtServices.GetUsername() || '-'
    },
    kpiAtinse(): number {
      return this.kpiItems.filter(k => k.ProcentRealizat >= 100).length
    },
    medieRealizare(): number {
      if (!this.kpiItems.length) return 0
      const suma = this.kpiItems.reduce((acc, k) => acc + (k.ProcentRealizat || 0), 0)
      return Math.round(suma / this.kpiItems.length)
    },
    medieColor(): string {
      return this.getKPIColor(this.medieRealizare)
    },
    medieColorRGB(): string {
      const m = this.medieRealizare
      if (m >= 100) return '76,175,80'
      if (m >= 70) return '255,152,0'
      return '244,67,54'
    },
  },

  mounted() {
    this.incarcaRolAgent()
    this.incarcaRaport()
  },

  methods: {
    async incarcaRolAgent() {
      try {
        const utilizator = JwtServices.GetUsername()
        if (!utilizator) return
        const today = new Date().toISOString()
        const res = await CrmKPIService.getAgenti({
          '$filter': `Utilizator eq '${utilizator.replace(/'/g, "''")}' and (DataEnd eq null or DataEnd ge ${today})`,
          '$orderby': 'DataStart desc',
          '$top': '1'
        })
        const items = res.data?.value || []
        this.tipAgent = items[0]?.TipAgent || ''
      } catch (e) {
        console.error('Error loading agent type:', e)
      }
    },

    async incarcaRaport() {
      this.loading = true
      this.expandedID = null
      this.detalii = {}
      try {
        const utilizator = JwtServices.GetUsername()
        if (!utilizator) return
        const res = await CrmKPIService.getRaport(utilizator, this.anSelectat, this.lunaSelectata)
        this.kpiItems = res.data || []
        if (this.kpiItems.length > 0) {
          this.tipAgent = this.kpiItems[0]?.TipAgent || this.tipAgent
        }
      } catch (e) {
        console.error('Error loading KPI report:', e)
        this.kpiItems = []
      } finally {
        this.loading = false
      }
    },

    async toggleDetaliu(kpi: any) {
      // Dacă e deja deschis, îl închidem
      if (this.expandedID === kpi.KPIDefinitieID) {
        this.expandedID = null
        return
      }

      // Deschidem cardul întotdeauna
      this.expandedID = kpi.KPIDefinitieID

      // SursaDate poate lipsi dacă backend-ul nu e actualizat — derivăm din Cod
      const sursaDate = this.getSursaDate(kpi)
      if (!sursaDate) return

      // Dacă datele sunt deja încărcate, nu le mai cerem
      if (this.detalii[kpi.KPIDefinitieID]) return

      this.loadingDetalii = { ...this.loadingDetalii, [kpi.KPIDefinitieID]: true }
      try {
        const utilizator = JwtServices.GetUsername()
        if (!utilizator) return
        const res = await CrmKPIService.getDetaliu(utilizator, this.anSelectat, this.lunaSelectata, sursaDate)
        this.detalii = { ...this.detalii, [kpi.KPIDefinitieID]: res.data || [] }
      } catch (e) {
        console.error('Error loading KPI detail:', e)
        this.detalii = { ...this.detalii, [kpi.KPIDefinitieID]: [] }
      } finally {
        this.loadingDetalii = { ...this.loadingDetalii, [kpi.KPIDefinitieID]: false }
      }
    },

    /** SursaDate explicit (dacă backend-ul e actualizat) sau derivat din Cod (fallback).
     *  Ex: HUNTER_COMPANII_NOI → COMPANII_NOI */
    getSursaDate(kpi: any): string {
      if (kpi.SursaDate) return kpi.SursaDate
      const cod: string = kpi.Cod || ''
      const idx = cod.indexOf('_')
      return idx > -1 ? cod.substring(idx + 1) : cod
    },

    // ── Helpers coloane detaliu ───────────────────────────────────────────────

    labelColoanaDetaliu(sursaDate: string): string {
      const map: Record<string, string> = {
        COMPANII_NOI: 'Companie',
        CONTACTE_NOI: 'Contact',
        INTALNIRI: 'Subiect / Tip',
        DEMO: 'Subiect / Tip',
        OPORTUNITATI_NOI: 'Oportunitate',
        CLIENTI_CONTACTATI: 'Client',
        INTALNIRI_PORTOFOLIU: 'Subiect / Tip',
        UPSELL_CROSSSELL: 'Subiect / Tip',
        OPORTUNITATI_PORTOFOLIU: 'Oportunitate',
        OFERTE_TRIMISE: 'Ofertă',
      }
      return map[sursaDate] || 'Denumire'
    },

    areClient(sursaDate: string): boolean {
      return ['CONTACTE_NOI', 'INTALNIRI', 'DEMO', 'OPORTUNITATI_NOI',
              'INTALNIRI_PORTOFOLIU', 'UPSELL_CROSSSELL', 'OPORTUNITATI_PORTOFOLIU',
              'OFERTE_TRIMISE'].includes(sursaDate)
    },

    areContact(sursaDate: string): boolean {
      return ['CONTACTE_NOI', 'CLIENTI_CONTACTATI'].includes(sursaDate)
    },

    labelContact(sursaDate: string): string {
      if (sursaDate === 'CONTACTE_NOI') return 'Email'
      if (sursaDate === 'CLIENTI_CONTACTATI') return 'Nr. activități'
      return 'Info'
    },

    areValoare(sursaDate: string): boolean {
      return ['OPORTUNITATI_NOI', 'OPORTUNITATI_PORTOFOLIU'].includes(sursaDate)
    },

    areStatus(sursaDate: string): boolean {
      return ['OPORTUNITATI_NOI', 'OPORTUNITATI_PORTOFOLIU'].includes(sursaDate)
    },

    // ── Formatare ─────────────────────────────────────────────────────────────

    formatData(val: string | null): string {
      if (!val) return '—'
      return new Date(val).toLocaleDateString('ro-RO')
    },

    formatValoare(val: number): string {
      return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val)
    },

    getKPIColor(procent: number): string {
      if (procent >= 100) return '#4CAF50'
      if (procent >= 70) return '#FF9800'
      return '#F44336'
    },

    navigheazaLaDetaliu(sursaDate: string, rowId: any) {
      if (['COMPANII_NOI', 'CLIENTI_CONTACTATI'].includes(sursaDate)) {
        this.$router.push({ path: '/crm/parteneri', query: { partenerId: String(rowId) } })
      } else if (sursaDate === 'CONTACTE_NOI') {
        this.$router.push({ path: '/crm/contacte', query: { contactId: String(rowId) } })
      } else if (['OPORTUNITATI_NOI', 'OPORTUNITATI_PORTOFOLIU', 'OFERTE_TRIMISE'].includes(sursaDate)) {
        this.$router.push({ path: '/crm/grid-oportunitati', query: { opportunityId: String(rowId) } })
      } else if (['INTALNIRI', 'DEMO', 'UPSELL_CROSSSELL', 'INTALNIRI_PORTOFOLIU'].includes(sursaDate)) {
        this.$router.push('/crm/activitati')
      }
    },

    isActiveRoute(route: string): boolean {
      return this.$route.path === route
    },
  }
})
</script>

<style scoped>
.kpi-dashboard {
  padding: 2rem;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
  min-height: 100vh;
}

/* ── Nav menu ── */
.nav-menu-card {
  border-radius: 16px !important;
  margin-bottom: 2rem;
  overflow: hidden;
}

.nav-menu {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
  background: white;
  overflow-x: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  text-decoration: none;
  color: #718096;
  font-weight: 500;
  font-size: 0.9375rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-item:hover {
  background: #f7fafc;
  color: #1a202c;
}

.nav-item.active {
  background: linear-gradient(135deg, rgba(255, 87, 34, 0.1) 0%, rgba(255, 87, 34, 0.05) 100%);
  color: var(--v-theme-primary);
  font-weight: 600;
}

/* ── Filter bar ── */
.filter-card {
  border-radius: 14px !important;
  padding: 1rem 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-label {
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
}

.filter-select {
  max-width: 140px;
}

.refresh-btn {
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  padding: 0 16px !important;
  height: 34px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  background: white !important;
  color: #4a5568 !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
  font-size: 0.8125rem !important;
}

.refresh-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 87, 34, 0.1) !important;
  background: rgba(255, 87, 34, 0.04) !important;
  color: #ff5722 !important;
  border-color: rgba(255, 87, 34, 0.3) !important;
}

.refresh-btn .v-icon {
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.refresh-btn:hover .v-icon {
  transform: rotate(180deg);
}

/* ── Summary cards ── */
.summary-card {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
}

.summary-inner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
}

.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1a202c;
  line-height: 1;
}

.summary-label {
  font-size: 0.75rem;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-top: 4px;
}

/* ── KPI card ── */
.kpi-card {
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  height: auto;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.07) !important;
}

.kpi-card--expanded {
  border-color: rgba(255, 87, 34, 0.2) !important;
  box-shadow: 0 4px 16px rgba(255, 87, 34, 0.08) !important;
}

.kpi-card-inner {
  padding: 1.25rem;
  user-select: none;
}

.kpi-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 4px;
}

.expand-icon {
  transition: transform 0.25s ease;
}

.expand-icon--open {
  transform: rotate(180deg);
}

.kpi-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.kpi-progress-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 0.5rem;
}

.kpi-realizat {
  font-size: 1.5rem;
  font-weight: 800;
}

.kpi-separator {
  font-size: 1rem;
  color: #a0aec0;
}

.kpi-target {
  font-size: 1rem;
  color: #718096;
  font-weight: 500;
}

.kpi-progress-bar-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.kpi-progress-bar {
  flex: 1;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.kpi-progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.kpi-percent {
  font-size: 0.875rem;
  font-weight: 700;
  min-width: 40px;
  text-align: right;
}

/* ── Detalii expand ── */
.kpi-detalii {
  border-top: 1px solid #f0f0f0;
  padding: 0.75rem 1.25rem 1rem;
  background: #fafbfc;
  border-radius: 0 0 14px 14px;
}

.detalii-empty {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  color: #a0aec0;
  padding: 0.5rem 0;
}

.detalii-header {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.detalii-scroll {
  max-height: 260px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid #edf2f7;
}

.detalii-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  background: white;
}

.detalii-table thead th {
  background: #f7fafc;
  padding: 0.45rem 0.65rem;
  text-align: left;
  font-size: 0.7rem;
  font-weight: 700;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #edf2f7;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.detalii-table tbody td {
  padding: 0.4rem 0.65rem;
  border-bottom: 1px solid #f7fafc;
  color: #2d3748;
  vertical-align: middle;
}

.detalii-table tbody tr:last-child td {
  border-bottom: none;
}

.detalii-table tbody tr:hover td {
  background: #f7fafc;
}

.col-denumire {
  font-weight: 500;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-client {
  color: #4a5568;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-contact {
  color: #718096;
  font-size: 0.75rem;
}

.col-valoare {
  text-align: right;
  font-weight: 600;
  color: #2d3748;
  white-space: nowrap;
}

.col-data {
  color: #a0aec0;
  white-space: nowrap;
  font-size: 0.75rem;
}

.detalii-row-link {
  cursor: pointer;
}

.detalii-row-link:hover td {
  background: #edf2f7 !important;
}

@media (max-width: 600px) {
  .kpi-dashboard {
    padding: 1rem;
  }

  .filter-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
